import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { OrgAdminSidebar } from '@/components/AdminSidebar';
import { cn } from '@/lib/utils';
import {
  Users, Building2, GraduationCap, BookOpen, Loader2, RefreshCw, Plus, Trash2, 
  Edit2, Save, X, Eye, Key, UserPlus, CreditCard, DollarSign, BarChart3, 
  TrendingUp, Calendar, Clock, Download, Upload, CheckCircle, AlertCircle,
  Bell, Settings, FileText, PieChart as PieChartIcon, School, Link2, Unlink, XCircle, Menu
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, AreaChart, Area } from 'recharts';
import KnowledgeBasePage from '@/pages/KnowledgeBasePage';
import CustomCurriculumSection from '@/components/CustomCurriculumSection';
import PasswordStrengthMeter, { isPasswordValid } from '@/components/PasswordStrengthMeter';
import RatioSelector from '@/components/RatioSelector';

const OrgAdminDashboardNew = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  // Sidebar state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');
  
  // Data states
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [users, setUsers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [licenseInfo, setLicenseInfo] = useState(null);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [billingHistory, setBillingHistory] = useState([]);
  const [billingStatus, setBillingStatus] = useState(null);
  const [reports, setReports] = useState(null);
  const [notifications, setNotifications] = useState([]);
  
  // Filter states
  const [userSearch, setUserSearch] = useState('');
  const [classSearch, setClassSearch] = useState('');
  
  // Dialog states
  const [addStaffDialog, setAddStaffDialog] = useState(false);
  const [addClassDialog, setAddClassDialog] = useState(false);
  const [addStudentDialog, setAddStudentDialog] = useState(false);
  const [deleteStudentDialog, setDeleteStudentDialog] = useState({ open: false, studentId: null, studentName: '' });
  const [resetPwdDialog, setResetPwdDialog] = useState({ open: false, userId: null, userName: '' });
  const [deleteUserDialog, setDeleteUserDialog] = useState({ open: false, userId: null, userName: '' });
  const [requestLicensesDialog, setRequestLicensesDialog] = useState(false);
  const [purchaseLicensesDialog, setPurchaseLicensesDialog] = useState(false);
  const [upgradeDialog, setUpgradeDialog] = useState(false);
  const [bulkUploadDialog, setBulkUploadDialog] = useState(false);
  const [bulkUploadFile, setBulkUploadFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkUploadResult, setBulkUploadResult] = useState(null);
  const [completeBillingDialog, setCompleteBillingDialog] = useState(false);
  const [completeBillingSeats, setCompleteBillingSeats] = useState(10);
  const [completeBillingPromo, setCompleteBillingPromo] = useState('');
  const [completeBillingLoading, setCompleteBillingLoading] = useState(false);
  
  // Form states
  const [newStaff, setNewStaff] = useState({ name: '', email: '', password: '', role: 'teacher' });
  const [newClass, setNewClass] = useState({ name: '', description: '', grade_level: '', max_students: 30, small_setting_ratio: '' });
  const [newStudent, setNewStudent] = useState({ name: '', birth_date: '', grade_level: '', class_id: '' });
  const [newPassword, setNewPassword] = useState('');
  const [licenseRequest, setLicenseRequest] = useState({ count: 10, reason: '' });
  const [purchaseCount, setPurchaseCount] = useState(10);
  
  // Loading states
  const [addingStaff, setAddingStaff] = useState(false);
  const [addingClass, setAddingClass] = useState(false);
  const [addingStudent, setAddingStudent] = useState(false);
  const [deletingStudent, setDeletingStudent] = useState(false);
  const [resettingPwd, setResettingPwd] = useState(false);
  const [deletingUser, setDeletingUser] = useState(false);
  const [requestingLicenses, setRequestingLicenses] = useState(false);
  const [purchasingLicenses, setPurchasingLicenses] = useState(false);
  
  // Google Classroom states
  const [gcStatus, setGcStatus] = useState(null);
  const [gcCourses, setGcCourses] = useState([]);
  const [gcLoading, setGcLoading] = useState(false);
  const [gcConnecting, setGcConnecting] = useState(false);
  const [gcSyncing, setGcSyncing] = useState({});

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (activeSection === 'users') fetchUsers();
    if (activeSection === 'students') { fetchClasses(); fetchStudents(); }
    if (activeSection === 'licenses') fetchLicenseInfo();
    if (activeSection === 'subscription') { fetchSubscriptionInfo(); fetchBillingHistory(); }
    if (activeSection === 'reports') fetchReports();
    if (activeSection === 'notifications') fetchNotifications();
    if (activeSection === 'google-classroom') fetchGoogleClassroomStatus();
  }, [activeSection]);

  const fetchInitialData = async () => {
    try {
      const response = await api.get('/org-admin/dashboard');
      setDashboardData(response.data);
      
      // Also fetch license info for overview
      try {
        const licenseRes = await api.get('/org-admin/licenses');
        setLicenseInfo(licenseRes.data);
      } catch (e) {
        // License endpoint might not exist yet
        setLicenseInfo({
          total: response.data?.organization?.student_licenses || 0,
          used: response.data?.stats?.students || 0,
          available: (response.data?.organization?.student_licenses || 0) - (response.data?.stats?.students || 0)
        });
      }

      // Billing gate: free-tier caps until Stripe checkout completes.
      try {
        const bs = await api.get('/org-admin/billing-status');
        setBillingStatus(bs.data);
      } catch (e) {
        // ignore
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      if (error.response?.status === 403) {
        toast.error('Organization admin access required');
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/org-admin/users');
      setUsers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchClasses = async () => {
    try {
      const response = await api.get('/org-admin/classes');
      setClasses(response.data || []);
    } catch (error) {
      console.error('Failed to fetch classes:', error);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await api.get('/org-admin/students');
      const payload = response.data;
      setStudents(Array.isArray(payload) ? payload : (payload?.students || []));
    } catch (error) {
      console.error('Failed to fetch students:', error);
    }
  };

  const fetchLicenseInfo = async () => {
    try {
      const response = await api.get('/org-admin/licenses');
      setLicenseInfo(response.data);
    } catch (error) {
      // Fallback to dashboard data
      if (dashboardData) {
        setLicenseInfo({
          total: dashboardData.organization?.student_licenses || 0,
          used: dashboardData.stats?.students || 0,
          available: (dashboardData.organization?.student_licenses || 0) - (dashboardData.stats?.students || 0),
          pending_requests: []
        });
      }
    }
  };

  const fetchSubscriptionInfo = async () => {
    try {
      const response = await api.get('/org-admin/subscription');
      setSubscriptionInfo(response.data);
    } catch (error) {
      // Fallback
      setSubscriptionInfo({
        plan: dashboardData?.organization?.subscription_plan || 'enterprise',
        status: 'active',
        price_per_student: 5.00,
        billing_cycle: 'monthly',
        next_billing_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
      });
    }
  };

  const fetchBillingHistory = async () => {
    try {
      const response = await api.get('/org-admin/billing-history');
      setBillingHistory(response.data || []);
    } catch (error) {
      // Mock data
      setBillingHistory([
        { id: '1', date: new Date().toISOString(), amount: 250, description: '50 licenses - Monthly', status: 'paid' },
        { id: '2', date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(), amount: 250, description: '50 licenses - Monthly', status: 'paid' }
      ]);
    }
  };

  const fetchReports = async () => {
    try {
      const response = await api.get('/org-admin/reports');
      setReports(response.data);
    } catch (error) {
      // Mock data
      setReports({
        activity_summary: {
          total_activities_completed: 156,
          average_score: 78,
          most_active_subject: 'Math'
        },
        engagement: {
          daily_active_students: 12,
          weekly_active_students: 35,
          monthly_active_students: 48
        },
        performance_by_subject: [
          { subject: 'Math', avgScore: 82, completed: 89 },
          { subject: 'Reading', avgScore: 75, completed: 76 },
          { subject: 'Science', avgScore: 80, completed: 54 },
          { subject: 'Writing', avgScore: 72, completed: 37 }
        ],
        weekly_trend: [
          { week: 'W1', activities: 45, students: 28 },
          { week: 'W2', activities: 52, students: 32 },
          { week: 'W3', activities: 48, students: 30 },
          { week: 'W4', activities: 61, students: 38 }
        ]
      });
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await api.get('/org-admin/notifications');
      setNotifications(response.data || []);
    } catch (error) {
      // Mock data
      setNotifications([
        { id: '1', type: 'info', message: 'Your subscription renews in 7 days', date: new Date().toISOString(), read: false },
        { id: '2', type: 'warning', message: 'License usage at 80%', date: new Date().toISOString(), read: false },
        { id: '3', type: 'success', message: '5 new students enrolled this week', date: new Date(Date.now() - 86400000).toISOString(), read: true }
      ]);
    }
  };

  // Google Classroom functions (real integration via /api/google-classroom/*)
  const fetchGoogleClassroomStatus = async () => {
    setGcLoading(true);
    try {
      const res = await api.get('/google-classroom/status');
      // Normalize to the shape existing UI expects.
      const normalized = {
        connected: !!res.data.connected,
        user_connected: !!res.data.connected,
        org_connected: !!res.data.connected,
        user_email: res.data.email,
        org_email: res.data.email,
        last_sync: res.data.updated_at,
      };
      setGcStatus(normalized);
      if (normalized.connected) fetchGoogleClassroomCourses();
    } catch (error) {
      console.error('Failed to fetch Google Classroom status:', error);
    } finally {
      setGcLoading(false);
    }
  };

  const fetchGoogleClassroomCourses = async () => {
    try {
      const res = await api.get('/google-classroom/courses');
      setGcCourses(res.data.courses || []);
    } catch (error) {
      if (error.response?.status === 409) {
        // Not connected; silently ignore.
      } else {
        console.error('Failed to fetch courses:', error);
      }
    }
  };

  const handleGoogleClassroomConnect = async () => {
    setGcConnecting(true);
    try {
      const res = await api.get('/google-classroom/authorize', {
        params: { return_path: '/organization/admin' },
      });
      if (res.data?.authorization_url) {
        window.location.assign(res.data.authorization_url);
      } else {
        toast.error('Failed to start Google OAuth flow.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start Google Classroom connect');
    } finally {
      setGcConnecting(false);
    }
  };

  const handleGoogleClassroomDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect Google Classroom?')) return;
    try {
      await api.post('/google-classroom/disconnect');
      toast.success('Google Classroom disconnected');
      setGcStatus({ connected: false, org_connected: false, user_connected: false });
      setGcCourses([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disconnect');
    }
  };

  const handleGoogleClassroomSync = async (courseId) => {
    setGcSyncing(prev => ({ ...prev, [courseId]: true }));
    try {
      const res = await api.post(`/google-classroom/courses/${courseId}/sync-roster`);
      toast.success(`Synced ${res.data.students_synced} students from Google Classroom.`);
      fetchClasses(); // Refresh classes
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync course');
    } finally {
      setGcSyncing(prev => ({ ...prev, [courseId]: false }));
    }
  };

  // User management handlers
  const handleAddStaff = async () => {
    if (!newStaff.name || !newStaff.email || !newStaff.password) {
      toast.error('Please fill all required fields');
      return;
    }
    setAddingStaff(true);
    try {
      await api.post('/org-admin/users', newStaff);
      toast.success('Staff member added');
      setAddStaffDialog(false);
      setNewStaff({ name: '', email: '', password: '', role: 'teacher' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add staff');
    } finally {
      setAddingStaff(false);
    }
  };

  const handleResetPassword = async () => {
    if (!isPasswordValid(newPassword || '')) {
      toast.error('Password must include upper, lower, number, and special character (min 8).');
      return;
    }
    setResettingPwd(true);
    try {
      await api.post(`/org-admin/users/${resetPwdDialog.userId}/reset-password`, { password: newPassword });
      toast.success('Password reset successfully');
      setResetPwdDialog({ open: false, userId: null, userName: '' });
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPwd(false);
    }
  };

  const handleDeleteUser = async () => {
    setDeletingUser(true);
    try {
      await api.delete(`/org-admin/users/${deleteUserDialog.userId}`);
      toast.success('User removed');
      setDeleteUserDialog({ open: false, userId: null, userName: '' });
      fetchUsers();
    } catch (error) {
      toast.error('Failed to remove user');
    } finally {
      setDeletingUser(false);
    }
  };

  // Class management
  const handleAddClass = async () => {
    if (!newClass.name) {
      toast.error('Please enter class name');
      return;
    }
    setAddingClass(true);
    try {
      await api.post('/org-admin/classes', newClass);
      toast.success('Class created');
      setAddClassDialog(false);
      setNewClass({ name: '', description: '', grade_level: '', max_students: 30, small_setting_ratio: '' });
      fetchClasses();
    } catch (error) {
      toast.error('Failed to create class');
    } finally {
      setAddingClass(false);
    }
  };

  // Student management
  const handleAddStudent = async () => {
    if (!newStudent.name) {
      toast.error('Please enter student name');
      return;
    }
    setAddingStudent(true);
    try {
      await api.post('/org-admin/students', newStudent);
      toast.success('Student added successfully');
      setAddStudentDialog(false);
      setNewStudent({ name: '', birth_date: '', grade_level: '', class_id: '' });
      fetchStudents();
      fetchInitialData(); // Refresh license info
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add student');
    } finally {
      setAddingStudent(false);
    }
  };

  const handleDeleteStudent = async () => {
    setDeletingStudent(true);
    try {
      await api.delete(`/org-admin/students/${deleteStudentDialog.studentId}`);
      toast.success('Student removed');
      setDeleteStudentDialog({ open: false, studentId: null, studentName: '' });
      fetchStudents();
      fetchInitialData(); // Refresh license info
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove student');
    } finally {
      setDeletingStudent(false);
    }
  };

  // Bulk student upload (CSV / XLSX)
  const handleBulkUpload = async () => {
    if (!bulkUploadFile) {
      toast.error('Please choose a CSV or XLSX file first.');
      return;
    }
    setBulkUploading(true);
    setBulkUploadResult(null);
    try {
      const form = new FormData();
      form.append('file', bulkUploadFile);
      const res = await api.post('/org-admin/students/bulk-upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setBulkUploadResult(res.data);
      toast.success(`Imported ${res.data.imported_count} student(s).`);
      fetchStudents();
      fetchInitialData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk upload failed');
    } finally {
      setBulkUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await api.get('/org-admin/students/bulk-template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'students_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Failed to download template');
    }
  };

  // Volume pricing tiers (must mirror backend /subscriptions/organization-checkout).
  const perStudentPrice = (n) => (n >= 100 ? 1.5 : n >= 50 ? 2.0 : n >= 25 ? 2.5 : 3.0);
  const completeBillingMonthly = (() => {
    const n = Number(completeBillingSeats) || 0;
    return (perStudentPrice(n) * n).toFixed(2);
  })();

  const handleCompleteBillingCheckout = async () => {
    const seats = Number(completeBillingSeats) || 0;
    if (seats < 1) {
      toast.error('Enter at least 1 seat.');
      return;
    }
    setCompleteBillingLoading(true);
    try {
      const params = new URLSearchParams({ student_count: String(seats) });
      if (completeBillingPromo.trim()) params.set('promo_code', completeBillingPromo.trim());
      const res = await api.post(`/subscriptions/organization-checkout?${params.toString()}`);
      if (res.data?.url) {
        window.location.assign(res.data.url);
      } else {
        toast.error('Could not start Stripe checkout.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setCompleteBillingLoading(false);
    }
  };

  // License management
  const handleRequestLicenses = async () => {
    if (licenseRequest.count < 1) {
      toast.error('Please enter valid license count');
      return;
    }
    setRequestingLicenses(true);
    try {
      await api.post('/org-admin/licenses/request', licenseRequest);
      toast.success('License request submitted');
      setRequestLicensesDialog(false);
      setLicenseRequest({ count: 10, reason: '' });
      fetchLicenseInfo();
    } catch (error) {
      toast.error('Failed to submit request');
    } finally {
      setRequestingLicenses(false);
    }
  };

  const handlePurchaseLicenses = async () => {
    if (purchaseCount < 1) {
      toast.error('Please enter valid count');
      return;
    }
    setPurchasingLicenses(true);
    try {
      // This would integrate with Stripe
      await api.post('/org-admin/licenses/purchase', { count: purchaseCount });
      toast.success(`Purchased ${purchaseCount} licenses`);
      setPurchaseLicensesDialog(false);
      setPurchaseCount(10);
      fetchLicenseInfo();
    } catch (error) {
      toast.error('Failed to purchase licenses');
    } finally {
      setPurchasingLicenses(false);
    }
  };

  const COLORS = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b'];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  const orgName = dashboardData?.organization?.name || 'Organization';

  return (
    <div className="min-h-screen bg-slate-100">
      <OrgAdminSidebar 
        collapsed={sidebarCollapsed} 
        setCollapsed={setSidebarCollapsed}
        activeSection={activeSection}
        setActiveSection={setActiveSection}
        orgName={orgName}
        mobileOpen={mobileNavOpen}
        onMobileClose={() => setMobileNavOpen(false)}
      />

      {/* Mobile top bar */}
      <div
        className="md:hidden sticky top-0 z-30 bg-white border-b border-slate-200 h-14 flex items-center justify-between px-3 shadow-sm"
        data-testid="org-mobile-topbar"
      >
        <button
          onClick={() => setMobileNavOpen(true)}
          className="p-2 -ml-2 rounded-md hover:bg-slate-100 text-slate-700"
          aria-label="Open menu"
          data-testid="org-mobile-menu-btn"
        >
          <Menu className="w-6 h-6" />
        </button>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-gradient-to-br from-teal-600 to-teal-700 rounded-lg flex items-center justify-center">
            <Building2 className="w-4 h-4 text-white" />
          </div>
          <span className="font-semibold text-slate-900 text-sm truncate max-w-[180px]">{orgName || 'Org Admin'}</span>
        </div>
        <div className="w-9" />
      </div>

      <main className={cn(
        "transition-all duration-300 p-4 md:p-6",
        sidebarCollapsed ? "md:ml-16" : "md:ml-64"
      )}>
        {/* Billing gate banner */}
        {billingStatus && billingStatus.billing_active === false && (
          <div className="mb-4 p-4 rounded-lg border border-amber-300 bg-amber-50 flex items-start gap-3" data-testid="billing-gate-banner">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-900">
                Free trial mode — Complete billing to unlock full access
              </p>
              <p className="text-xs text-amber-800 mt-1">
                Your organization is capped at <strong>{billingStatus.free_tier?.max_teachers ?? 1} teacher</strong> and <strong>{billingStatus.free_tier?.max_students ?? 5} students</strong> until Stripe checkout is completed.{' '}
                {billingStatus.free_tier?.students_remaining !== null && (
                  <>Currently: {billingStatus.teacher_count}/{billingStatus.free_tier?.max_teachers} teachers, {billingStatus.used_licenses}/{billingStatus.free_tier?.max_students} students.</>
                )}
              </p>
              <Button
                size="sm"
                className="mt-2 bg-amber-600 hover:bg-amber-700 text-white"
                onClick={() => { setCompleteBillingSeats(Math.max(billingStatus.used_licenses || 10, 10)); setCompleteBillingDialog(true); }}
                data-testid="complete-billing-btn"
              >
                <CreditCard className="w-4 h-4 mr-2" /> Complete Billing
              </Button>
            </div>
          </div>
        )}

        {/* Overview Section */}
        {activeSection === 'overview' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-slate-800">{orgName}</h1>
                <p className="text-sm text-slate-500">Organization Dashboard</p>
              </div>
              <Button variant="outline" onClick={fetchInitialData}>
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh
              </Button>
            </div>
            
            {/* Stats Grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="cursor-pointer hover:shadow-lg transition-all" onClick={() => setActiveSection('users')}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <Users className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Staff Members</p>
                      <p className="text-2xl font-bold text-slate-900">{dashboardData?.stats?.teachers || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="cursor-pointer hover:shadow-lg transition-all" onClick={() => setActiveSection('students')}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                      <GraduationCap className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Students</p>
                      <p className="text-2xl font-bold text-slate-900">{dashboardData?.stats?.students || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="cursor-pointer hover:shadow-lg transition-all" onClick={() => setActiveSection('students')}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                      <BookOpen className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Classes</p>
                      <p className="text-2xl font-bold text-slate-900">{dashboardData?.stats?.classes || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="cursor-pointer hover:shadow-lg transition-all" onClick={() => setActiveSection('licenses')}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                      <CreditCard className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Licenses</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {licenseInfo?.used || 0}/{licenseInfo?.total || 0}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions */}
            <div className="grid lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button className="w-full justify-start" variant="outline" onClick={() => setAddStaffDialog(true)}>
                    <UserPlus className="w-4 h-4 mr-2" /> Add Staff Member
                  </Button>
                  <Button className="w-full justify-start" variant="outline" onClick={() => setAddStudentDialog(true)} data-testid="quick-add-student">
                    <GraduationCap className="w-4 h-4 mr-2" /> Add Student
                  </Button>
                  <Button className="w-full justify-start" variant="outline" onClick={() => setAddClassDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" /> Create Class
                  </Button>
                  <Button className="w-full justify-start" variant="outline" onClick={() => setActiveSection('reports')}>
                    <BarChart3 className="w-4 h-4 mr-2" /> View Reports
                  </Button>
                  <Button className="w-full justify-start" variant="outline" onClick={() => setActiveSection('licenses')}>
                    <CreditCard className="w-4 h-4 mr-2" /> Manage Licenses
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>License Usage</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Used</span>
                      <span className="font-medium">{licenseInfo?.used || 0} / {licenseInfo?.total || 0}</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-3">
                      <div 
                        className={cn(
                          "h-3 rounded-full transition-all",
                          ((licenseInfo?.used || 0) / (licenseInfo?.total || 1)) > 0.9 ? 'bg-red-500' :
                          ((licenseInfo?.used || 0) / (licenseInfo?.total || 1)) > 0.7 ? 'bg-yellow-500' :
                          'bg-green-500'
                        )}
                        style={{ width: `${Math.min(100, ((licenseInfo?.used || 0) / (licenseInfo?.total || 1)) * 100)}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Available</span>
                      <span className="font-medium text-green-600">{licenseInfo?.available || 0}</span>
                    </div>
                    {((licenseInfo?.used || 0) / (licenseInfo?.total || 1)) > 0.8 && (
                      <Button className="w-full" size="sm" onClick={() => setPurchaseLicensesDialog(true)}>
                        Purchase More Licenses
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* User Management Section */}
        {activeSection === 'users' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-slate-800">User Management</h1>
              <Button onClick={() => setAddStaffDialog(true)}>
                <UserPlus className="w-4 h-4 mr-2" /> Add Staff
              </Button>
            </div>
            
            <Card>
              <CardContent className="p-4">
                <div className="relative">
                  <Input
                    placeholder="Search users..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.filter(u => 
                      u.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
                      u.email?.toLowerCase().includes(userSearch.toLowerCase())
                    ).map((u) => (
                      <TableRow key={u.id}>
                        <TableCell className="font-medium">{u.name}</TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell className="capitalize">
                          <span className={cn(
                            "px-2 py-1 rounded-full text-xs",
                            u.account_type === 'org_admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                          )}>
                            {u.account_type?.replace('_', ' ')}
                          </span>
                        </TableCell>
                        <TableCell>{u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => setResetPwdDialog({ open: true, userId: u.id, userName: u.name })}
                            >
                              <Key className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-red-500"
                              onClick={() => setDeleteUserDialog({ open: true, userId: u.id, userName: u.name })}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Students & Classes Section */}
        {activeSection === 'students' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-slate-800">Students & Classes</h1>
              <Button onClick={() => setAddClassDialog(true)}>
                <Plus className="w-4 h-4 mr-2" /> Create Class
              </Button>
            </div>
            
            {/* Classes */}
            <Card>
              <CardHeader>
                <CardTitle>Classes ({classes.length})</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Class Name</TableHead>
                      <TableHead>Grade</TableHead>
                      <TableHead>Students</TableHead>
                      <TableHead>Max</TableHead>
                      <TableHead>Small setting</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {classes.map((cls) => (
                      <TableRow key={cls.id}>
                        <TableCell className="font-medium">{cls.name}</TableCell>
                        <TableCell>{cls.grade_level || 'N/A'}</TableCell>
                        <TableCell>{cls.student_ids?.length || 0}</TableCell>
                        <TableCell>{cls.max_students || 30}</TableCell>
                        <TableCell>
                          {cls.small_setting_ratio ? (
                            <span
                              className="inline-flex items-center gap-1 rounded-full bg-teal-50 border border-teal-200 text-teal-800 text-[11px] font-semibold px-2 py-0.5"
                              data-testid={`class-ratio-badge-${cls.id}`}
                            >
                              {cls.small_setting_ratio}
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Students */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Students ({students.length})</CardTitle>
                <div className="flex gap-2">
                  <Button
                    onClick={() => { setBulkUploadDialog(true); setBulkUploadFile(null); setBulkUploadResult(null); }}
                    size="sm"
                    variant="outline"
                    data-testid="bulk-upload-students-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" /> Bulk Upload
                  </Button>
                  <Button onClick={() => setAddStudentDialog(true)} size="sm" data-testid="add-student-btn">
                    <UserPlus className="w-4 h-4 mr-2" /> Add Student
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Grade</TableHead>
                      <TableHead>Level</TableHead>
                      <TableHead>XP</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {students.slice(0, 10).map((s) => (
                      <TableRow key={s.id}>
                        <TableCell className="font-medium">{s.name}</TableCell>
                        <TableCell>{s.grade_level || 'N/A'}</TableCell>
                        <TableCell>
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                            Level {s.current_level || 1}
                          </span>
                        </TableCell>
                        <TableCell>{s.total_xp || 0}</TableCell>
                        <TableCell className="text-right">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => setDeleteStudentDialog({ open: true, studentId: s.id, studentName: s.name })}
                            data-testid={`delete-student-${s.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {students.length === 0 && (
                  <div className="p-8 text-center text-slate-500">
                    <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No students yet. Click "Add Student" to get started.</p>
                    <p className="text-sm mt-2">{licenseInfo?.available || 0} licenses available</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* License Management Section */}
        {activeSection === 'licenses' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-slate-800">License Management</h1>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setRequestLicensesDialog(true)}>
                  <FileText className="w-4 h-4 mr-2" /> Request Licenses
                </Button>
                <Button onClick={() => setPurchaseLicensesDialog(true)}>
                  <CreditCard className="w-4 h-4 mr-2" /> Purchase Licenses
                </Button>
              </div>
            </div>
            
            {/* License Overview */}
            <div className="grid md:grid-cols-3 gap-6">
              <Card>
                <CardContent className="p-6 text-center">
                  <CreditCard className="w-12 h-12 mx-auto mb-4 text-blue-500" />
                  <p className="text-3xl font-bold text-slate-900">{licenseInfo?.total || 0}</p>
                  <p className="text-sm text-slate-500">Total Licenses</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6 text-center">
                  <Users className="w-12 h-12 mx-auto mb-4 text-green-500" />
                  <p className="text-3xl font-bold text-slate-900">{licenseInfo?.used || 0}</p>
                  <p className="text-sm text-slate-500">Used</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6 text-center">
                  <CheckCircle className="w-12 h-12 mx-auto mb-4 text-emerald-500" />
                  <p className="text-3xl font-bold text-emerald-600">{licenseInfo?.available || 0}</p>
                  <p className="text-sm text-slate-500">Available</p>
                </CardContent>
              </Card>
            </div>

            {/* Usage Progress */}
            <Card>
              <CardHeader>
                <CardTitle>License Usage</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span>Usage</span>
                    <span className="font-medium">
                      {Math.round(((licenseInfo?.used || 0) / (licenseInfo?.total || 1)) * 100)}%
                    </span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-4">
                    <div 
                      className={cn(
                        "h-4 rounded-full transition-all",
                        ((licenseInfo?.used || 0) / (licenseInfo?.total || 1)) > 0.9 ? 'bg-red-500' :
                        ((licenseInfo?.used || 0) / (licenseInfo?.total || 1)) > 0.7 ? 'bg-yellow-500' :
                        'bg-green-500'
                      )}
                      style={{ width: `${Math.min(100, ((licenseInfo?.used || 0) / (licenseInfo?.total || 1)) * 100)}%` }}
                    />
                  </div>
                  <p className="text-sm text-slate-500">
                    {licenseInfo?.available || 0} licenses available for new students
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Pricing Info */}
            <Card>
              <CardHeader>
                <CardTitle>License Pricing</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500">1-50 licenses</p>
                    <p className="text-xl font-bold">$5.00 <span className="text-sm font-normal">/student/mo</span></p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
                    <p className="text-sm text-slate-500">51-100 licenses</p>
                    <p className="text-xl font-bold">$4.00 <span className="text-sm font-normal">/student/mo</span></p>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <p className="text-sm text-slate-500">100+ licenses</p>
                    <p className="text-xl font-bold">$3.50 <span className="text-sm font-normal">/student/mo</span></p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Subscription Section */}
        {activeSection === 'subscription' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-slate-800">Subscription Management</h1>
              <Button onClick={() => setUpgradeDialog(true)}>
                <TrendingUp className="w-4 h-4 mr-2" /> Upgrade Plan
              </Button>
            </div>
            
            {/* Current Plan */}
            <Card>
              <CardHeader>
                <CardTitle>Current Plan</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg">
                  <div>
                    <p className="text-2xl font-bold capitalize">{subscriptionInfo?.plan || 'Enterprise'}</p>
                    <p className="text-sm text-slate-500">Active subscription</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold">${subscriptionInfo?.price_per_student || 5.00}/student</p>
                    <p className="text-sm text-slate-500">per month</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Billing Cycle</p>
                    <p className="font-medium capitalize">{subscriptionInfo?.billing_cycle || 'Monthly'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Next Billing Date</p>
                    <p className="font-medium">
                      {subscriptionInfo?.next_billing_date 
                        ? new Date(subscriptionInfo.next_billing_date).toLocaleDateString() 
                        : 'N/A'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Billing History */}
            <Card>
              <CardHeader>
                <CardTitle>Billing History</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {billingHistory.map((bill) => (
                      <TableRow key={bill.id}>
                        <TableCell>{new Date(bill.date).toLocaleDateString()}</TableCell>
                        <TableCell>{bill.description}</TableCell>
                        <TableCell className="font-medium">${bill.amount}</TableCell>
                        <TableCell>
                          <span className={cn(
                            "px-2 py-1 rounded-full text-xs",
                            bill.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                          )}>
                            {bill.status}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Button size="sm" variant="ghost">
                            <Download className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Reports Section */}
        {activeSection === 'reports' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-slate-800">Reports & Analytics</h1>
              <Button variant="outline">
                <Download className="w-4 h-4 mr-2" /> Export Report
              </Button>
            </div>
            
            {/* Summary Stats */}
            <div className="grid md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold">{reports?.activity_summary?.total_activities_completed || 0}</p>
                  <p className="text-xs text-slate-500">Activities Completed</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-green-600">{reports?.activity_summary?.average_score || 0}%</p>
                  <p className="text-xs text-slate-500">Average Score</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold">{reports?.engagement?.weekly_active_students || 0}</p>
                  <p className="text-xs text-slate-500">Weekly Active</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold">{reports?.activity_summary?.most_active_subject || 'N/A'}</p>
                  <p className="text-xs text-slate-500">Top Subject</p>
                </CardContent>
              </Card>
            </div>

            {/* Charts */}
            <div className="grid lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Performance by Subject</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={reports?.performance_by_subject || []}>
                        <XAxis dataKey="subject" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="avgScore" fill="#3b82f6" name="Avg Score" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Weekly Activity Trend</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={reports?.weekly_trend || []}>
                        <XAxis dataKey="week" />
                        <YAxis />
                        <Tooltip />
                        <Line type="monotone" dataKey="activities" stroke="#10b981" strokeWidth={2} />
                        <Line type="monotone" dataKey="students" stroke="#3b82f6" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Engagement Stats */}
            <Card>
              <CardHeader>
                <CardTitle>Student Engagement</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-6">
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <p className="text-3xl font-bold text-green-600">{reports?.engagement?.daily_active_students || 0}</p>
                    <p className="text-sm text-slate-500">Daily Active</p>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <p className="text-3xl font-bold text-blue-600">{reports?.engagement?.weekly_active_students || 0}</p>
                    <p className="text-sm text-slate-500">Weekly Active</p>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <p className="text-3xl font-bold text-purple-600">{reports?.engagement?.monthly_active_students || 0}</p>
                    <p className="text-sm text-slate-500">Monthly Active</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Notifications Section */}
        {activeSection === 'notifications' && (
          <div className="space-y-6">
            <h1 className="text-2xl font-bold text-slate-800">Notifications</h1>
            
            <div className="space-y-4">
              {notifications.map((notif) => (
                <Card key={notif.id} className={cn(!notif.read && 'border-l-4 border-blue-500')}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className={cn(
                        "w-10 h-10 rounded-full flex items-center justify-center",
                        notif.type === 'warning' ? 'bg-yellow-100' :
                        notif.type === 'success' ? 'bg-green-100' : 'bg-blue-100'
                      )}>
                        {notif.type === 'warning' ? <AlertCircle className="w-5 h-5 text-yellow-600" /> :
                         notif.type === 'success' ? <CheckCircle className="w-5 h-5 text-green-600" /> :
                         <Bell className="w-5 h-5 text-blue-600" />}
                      </div>
                      <div className="flex-1">
                        <p className={cn("font-medium", !notif.read && 'font-semibold')}>{notif.message}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {new Date(notif.date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Settings Section */}
        {activeSection === 'settings' && (
          <div className="space-y-6">
            <h1 className="text-2xl font-bold text-slate-800">Organization Settings</h1>
            <Card>
              <CardContent className="p-12 text-center text-slate-500">
                <Settings className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>Organization settings coming soon</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Google Classroom Section */}
        {activeSection === 'google-classroom' && (
          <div className="space-y-6">
            <h1 className="text-2xl font-bold text-slate-800">Google Classroom Integration</h1>
            
            {/* Connection Status Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      {gcStatus?.org_connected || gcStatus?.user_connected ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <XCircle className="w-5 h-5 text-slate-400" />
                      )}
                      Connection Status
                    </CardTitle>
                    <CardDescription>
                      {gcStatus?.org_connected 
                        ? `Organization connected as ${gcStatus.org_email}`
                        : gcStatus?.user_connected
                          ? `Your account connected as ${gcStatus.user_email}`
                          : 'Connect Google Classroom to import classes and sync students'
                      }
                    </CardDescription>
                  </div>
                  {gcStatus?.org_connected || gcStatus?.user_connected ? (
                    <Button 
                      variant="outline" 
                      onClick={handleGoogleClassroomDisconnect} 
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid="gc-disconnect-btn"
                    >
                      <Unlink className="w-4 h-4 mr-2" />
                      Disconnect
                    </Button>
                  ) : (
                    <Button 
                      onClick={handleGoogleClassroomConnect} 
                      disabled={gcConnecting}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="gc-connect-btn"
                    >
                      {gcConnecting ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Link2 className="w-4 h-4 mr-2" />
                      )}
                      Connect Google Classroom
                    </Button>
                  )}
                </div>
              </CardHeader>
              {(gcStatus?.org_connected || gcStatus?.user_connected) && gcStatus?.last_sync && (
                <CardContent>
                  <p className="text-sm text-slate-500">
                    Last synced: {new Date(gcStatus.last_sync).toLocaleString()}
                  </p>
                </CardContent>
              )}
            </Card>

            {/* Info Banner */}
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-800">Organization-Wide Integration</p>
                  <p className="text-sm text-blue-700">
                    When you connect Google Classroom, all teachers in your organization can import 
                    their classes and sync student rosters directly from Google Classroom.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Courses List */}
            {(gcStatus?.org_connected || gcStatus?.user_connected) && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Available Courses</CardTitle>
                    <Button variant="outline" size="sm" onClick={fetchGoogleClassroomCourses}>
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Refresh
                    </Button>
                  </div>
                  <CardDescription>
                    Select courses to sync to Zazu. Students will be automatically imported.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {gcLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                    </div>
                  ) : gcCourses.length === 0 ? (
                    <div className="text-center py-12">
                      <School className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">No courses found in Google Classroom</p>
                      <p className="text-sm text-slate-400 mt-1">Make sure you have active courses in your Google Classroom account</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {gcCourses.map((course) => (
                        <div 
                          key={course.id}
                          className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                              <BookOpen className="w-6 h-6 text-blue-600" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-slate-800">{course.name}</h3>
                              <p className="text-sm text-slate-500">{course.section}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs bg-slate-200 px-2 py-0.5 rounded-full text-slate-600 flex items-center gap-1">
                                  <Users className="w-3 h-3" />
                                  {course.student_count} students
                                </span>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  course.state === 'ACTIVE' 
                                    ? 'bg-green-100 text-green-700' 
                                    : 'bg-slate-200 text-slate-600'
                                }`}>
                                  {course.state}
                                </span>
                              </div>
                            </div>
                          </div>
                          <Button
                            onClick={() => handleGoogleClassroomSync(course.id)}
                            disabled={gcSyncing[course.id]}
                            className="bg-emerald-600 hover:bg-emerald-700"
                            data-testid={`gc-sync-${course.id}`}
                          >
                            {gcSyncing[course.id] ? (
                              <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                              <Download className="w-4 h-4 mr-2" />
                            )}
                            Sync to Zazu
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Not Connected State */}
            {!gcStatus?.org_connected && !gcStatus?.user_connected && !gcLoading && (
              <Card>
                <CardContent className="p-12 text-center">
                  <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <School className="w-10 h-10 text-blue-600" />
                  </div>
                  <h3 className="font-bold text-xl text-slate-800 mb-2">
                    Connect Google Classroom
                  </h3>
                  <p className="text-slate-600 mb-6 max-w-md mx-auto">
                    Import your organization's classes and students from Google Classroom. 
                    Teachers can then easily assign Zazu activities and track student progress.
                  </p>
                  <ul className="text-left max-w-sm mx-auto mb-6 space-y-2">
                    <li className="flex items-center gap-2 text-slate-600">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Import classes automatically
                    </li>
                    <li className="flex items-center gap-2 text-slate-600">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Sync student rosters
                    </li>
                    <li className="flex items-center gap-2 text-slate-600">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Available to all teachers in your organization
                    </li>
                  </ul>
                  <Button 
                    onClick={handleGoogleClassroomConnect} 
                    disabled={gcConnecting}
                    size="lg"
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="gc-connect-main-btn"
                  >
                    {gcConnecting ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Link2 className="w-4 h-4 mr-2" />
                    )}
                    Connect Now
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Knowledge Base Section */}
        {activeSection === 'knowledge-base' && (
          <KnowledgeBasePage />
        )}

        {/* Custom Curriculum Section */}
        {activeSection === 'curricula' && (
          <CustomCurriculumSection api={api} />
        )}
      </main>
      <Dialog open={addStaffDialog} onOpenChange={setAddStaffDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Staff Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={newStaff.name}
                onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={newStaff.email}
                onChange={(e) => setNewStaff({ ...newStaff, email: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Password *</Label>
              <Input
                type="password"
                value={newStaff.password}
                onChange={(e) => setNewStaff({ ...newStaff, password: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={newStaff.role} onValueChange={(v) => setNewStaff({ ...newStaff, role: v })}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="teacher">Teacher</SelectItem>
                  <SelectItem value="org_admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddStaffDialog(false)}>Cancel</Button>
            <Button onClick={handleAddStaff} disabled={addingStaff}>
              {addingStaff && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Add Staff
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Class Dialog */}
      <Dialog open={addClassDialog} onOpenChange={setAddClassDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Class</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Class Name *</Label>
              <Input
                value={newClass.name}
                onChange={(e) => setNewClass({ ...newClass, name: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Grade Level</Label>
              <Input
                value={newClass.grade_level}
                onChange={(e) => setNewClass({ ...newClass, grade_level: e.target.value })}
                className="mt-1"
                placeholder="e.g., K, 1, 2"
              />
            </div>
            <div>
              <Label>Max Students</Label>
              <Input
                type="number"
                value={newClass.max_students}
                onChange={(e) => setNewClass({ ...newClass, max_students: parseInt(e.target.value) || 30 })}
                className="mt-1"
              />
            </div>
            <RatioSelector
              value={newClass.small_setting_ratio}
              onChange={(v) => setNewClass({ ...newClass, small_setting_ratio: v })}
              label="Small-setting ratio (optional)"
              dataTestid="new-class-ratio"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddClassDialog(false)}>Cancel</Button>
            <Button onClick={handleAddClass} disabled={addingClass}>
              {addingClass && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Class
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete Billing Dialog — in-dashboard, no /pricing detour */}
      <Dialog open={completeBillingDialog} onOpenChange={setCompleteBillingDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Complete Billing — Organization Seats</DialogTitle>
            <DialogDescription>Choose your seat count and go straight to Stripe. Pricing updates live.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-600">
              Pick how many student seats you need. You'll be sent to Stripe next — no pricing page detour.
            </p>
            <div>
              <Label htmlFor="cb-seats">Number of students</Label>
              <Input
                id="cb-seats"
                type="number"
                min={1}
                value={completeBillingSeats}
                onChange={(e) => setCompleteBillingSeats(Number(e.target.value))}
                data-testid="complete-billing-seats-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Volume tiers: 1–24 → $3.00 · 25–49 → $2.50 · 50–99 → $2.00 · 100+ → $1.50 per student / month.
              </p>
            </div>
            <div>
              <Label htmlFor="cb-promo">Promo code (optional)</Label>
              <Input
                id="cb-promo"
                value={completeBillingPromo}
                onChange={(e) => setCompleteBillingPromo(e.target.value)}
                placeholder="e.g. TAKE50"
                data-testid="complete-billing-promo-input"
              />
            </div>
            <div className="p-3 rounded-lg bg-teal-50 border border-teal-200" data-testid="complete-billing-preview">
              <p className="text-sm text-teal-700">Your monthly total</p>
              <p className="text-2xl font-bold text-teal-900">
                ${completeBillingMonthly}
                <span className="text-sm font-normal text-teal-700 ml-2">
                  / month · ${perStudentPrice(Number(completeBillingSeats) || 0).toFixed(2)}/student × {completeBillingSeats || 0} students
                </span>
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteBillingDialog(false)}>Cancel</Button>
            <Button
              onClick={handleCompleteBillingCheckout}
              disabled={completeBillingLoading || !completeBillingSeats}
              data-testid="submit-complete-billing-btn"
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              {completeBillingLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <CreditCard className="w-4 h-4 mr-2" /> Continue to Stripe
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Students Dialog */}
      <Dialog open={bulkUploadDialog} onOpenChange={(o) => { setBulkUploadDialog(o); if (!o) { setBulkUploadFile(null); setBulkUploadResult(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Upload Students</DialogTitle>
            <DialogDescription>Import many students at once from a CSV or Excel file. Only the "name" column is required.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-600">
              Upload a <strong>CSV</strong> or <strong>Excel (.xlsx)</strong> file. Required column: <code>name</code>.
              Optional columns: <code>birth_date</code> (YYYY-MM-DD), <code>grade_level</code>, <code>class_name</code>
              (must match an existing class exactly).
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadTemplate}
              data-testid="download-bulk-template-btn"
            >
              <Download className="w-4 h-4 mr-2" /> Download CSV Template
            </Button>
            <div>
              <Label htmlFor="bulk-file-input">File</Label>
              <Input
                id="bulk-file-input"
                type="file"
                accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => setBulkUploadFile(e.target.files?.[0] || null)}
                className="mt-1"
                data-testid="bulk-upload-file-input"
              />
            </div>
            {bulkUploadResult && (
              <div className="bg-slate-50 p-3 rounded-lg text-sm space-y-1">
                <p className="text-emerald-700">
                  <CheckCircle className="w-4 h-4 inline mr-1" />
                  Imported {bulkUploadResult.imported_count} student(s).
                </p>
                {bulkUploadResult.errors?.length > 0 && (
                  <div className="mt-2">
                    <p className="text-red-600 font-medium">Errors ({bulkUploadResult.errors.length}):</p>
                    <ul className="text-xs text-slate-600 list-disc list-inside max-h-32 overflow-auto">
                      {bulkUploadResult.errors.slice(0, 20).map((er, i) => (
                        <li key={i}>Row {er.row}: {er.error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkUploadDialog(false)}>Close</Button>
            <Button onClick={handleBulkUpload} disabled={!bulkUploadFile || bulkUploading} data-testid="submit-bulk-upload-btn">
              {bulkUploading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Upload & Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Student Dialog */}
      <Dialog open={addStudentDialog} onOpenChange={setAddStudentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Student</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Student Name *</Label>
              <Input
                value={newStudent.name}
                onChange={(e) => setNewStudent({ ...newStudent, name: e.target.value })}
                className="mt-1"
                placeholder="Enter student's full name"
                data-testid="student-name-input"
              />
            </div>
            <div>
              <Label>Birth Date</Label>
              <Input
                type="date"
                value={newStudent.birth_date}
                onChange={(e) => setNewStudent({ ...newStudent, birth_date: e.target.value })}
                className="mt-1"
                data-testid="student-birthdate-input"
              />
            </div>
            <div>
              <Label>Grade Level</Label>
              <Select 
                value={newStudent.grade_level} 
                onValueChange={(value) => setNewStudent({ ...newStudent, grade_level: value })}
              >
                <SelectTrigger className="mt-1" data-testid="student-grade-select">
                  <SelectValue placeholder="Select grade level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pre-K">Pre-K</SelectItem>
                  <SelectItem value="K">Kindergarten</SelectItem>
                  <SelectItem value="1">1st Grade</SelectItem>
                  <SelectItem value="2">2nd Grade</SelectItem>
                  <SelectItem value="3">3rd Grade</SelectItem>
                  <SelectItem value="4">4th Grade</SelectItem>
                  <SelectItem value="5">5th Grade</SelectItem>
                  <SelectItem value="6">6th Grade</SelectItem>
                  <SelectItem value="7">7th Grade</SelectItem>
                  <SelectItem value="8">8th Grade</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {classes.length > 0 && (
              <div>
                <Label>Assign to Class (Optional)</Label>
                <Select 
                  value={newStudent.class_id} 
                  onValueChange={(value) => setNewStudent({ ...newStudent, class_id: value })}
                >
                  <SelectTrigger className="mt-1" data-testid="student-class-select">
                    <SelectValue placeholder="Select a class" />
                  </SelectTrigger>
                  <SelectContent>
                    {classes.map((cls) => (
                      <SelectItem key={cls.id} value={cls.id}>{cls.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="bg-slate-50 p-3 rounded-lg text-sm text-slate-600">
              <p>📋 Licenses available: <strong>{licenseInfo?.available || 0}</strong></p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddStudentDialog(false)}>Cancel</Button>
            <Button onClick={handleAddStudent} disabled={addingStudent} data-testid="submit-add-student">
              {addingStudent && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Add Student
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Student Dialog */}
      <Dialog open={deleteStudentDialog.open} onOpenChange={(open) => setDeleteStudentDialog({ ...deleteStudentDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Student</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600">
              Are you sure you want to remove <strong>{deleteStudentDialog.studentName}</strong> from the organization?
            </p>
            <p className="text-sm text-slate-500 mt-2">
              This action will free up one license and remove the student from all classes.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteStudentDialog({ open: false, studentId: null, studentName: '' })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteStudent} disabled={deletingStudent} data-testid="confirm-delete-student">
              {deletingStudent && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Remove Student
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPwdDialog.open} onOpenChange={(open) => setResetPwdDialog({ ...resetPwdDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password for {resetPwdDialog.userName}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label>New Password</Label>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="mt-1"
              placeholder="Create a strong password"
            />
            <div className="mt-2">
              <PasswordStrengthMeter password={newPassword} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetPwdDialog({ open: false, userId: null, userName: '' })}>Cancel</Button>
            <Button onClick={handleResetPassword} disabled={resettingPwd}>
              {resettingPwd && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={deleteUserDialog.open} onOpenChange={(open) => setDeleteUserDialog({ ...deleteUserDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Remove User</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>Are you sure you want to remove <strong>{deleteUserDialog.userName}</strong>?</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteUserDialog({ open: false, userId: null, userName: '' })}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteUser} disabled={deletingUser}>
              {deletingUser && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Remove User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Licenses Dialog */}
      <Dialog open={requestLicensesDialog} onOpenChange={setRequestLicensesDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Additional Licenses</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Number of Licenses</Label>
              <Input
                type="number"
                value={licenseRequest.count}
                onChange={(e) => setLicenseRequest({ ...licenseRequest, count: parseInt(e.target.value) || 0 })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Reason (optional)</Label>
              <Textarea
                value={licenseRequest.reason}
                onChange={(e) => setLicenseRequest({ ...licenseRequest, reason: e.target.value })}
                className="mt-1"
                placeholder="e.g., New semester enrollment"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRequestLicensesDialog(false)}>Cancel</Button>
            <Button onClick={handleRequestLicenses} disabled={requestingLicenses}>
              {requestingLicenses && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Purchase Licenses Dialog */}
      <Dialog open={purchaseLicensesDialog} onOpenChange={setPurchaseLicensesDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Purchase Licenses</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Number of Licenses</Label>
              <Input
                type="number"
                value={purchaseCount}
                onChange={(e) => setPurchaseCount(parseInt(e.target.value) || 0)}
                className="mt-1"
              />
            </div>
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="flex justify-between">
                <span>Licenses</span>
                <span>{purchaseCount}</span>
              </div>
              <div className="flex justify-between mt-2">
                <span>Price per license</span>
                <span>$5.00/mo</span>
              </div>
              <hr className="my-2" />
              <div className="flex justify-between font-bold">
                <span>Monthly Total</span>
                <span>${(purchaseCount * 5).toFixed(2)}</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPurchaseLicensesDialog(false)}>Cancel</Button>
            <Button onClick={handlePurchaseLicenses} disabled={purchasingLicenses}>
              {purchasingLicenses && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <CreditCard className="w-4 h-4 mr-2" /> Purchase
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upgrade Dialog */}
      <Dialog open={upgradeDialog} onOpenChange={setUpgradeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upgrade Plan</DialogTitle>
          </DialogHeader>
          <div className="py-4 text-center text-slate-500">
            <p>Contact sales for enterprise plan upgrades</p>
            <p className="text-sm mt-2">sales@zazu.edu</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUpgradeDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrgAdminDashboardNew;
